// 推廣歸因參數的瀏覽器端 I/O 組裝 —— TI-15642 / JT-1785738540。
// utils/referralParams.js 刻意保持純函數（無 import、可被獨立載入），因此把「真的碰
// localStorage / cookie」的部分獨立在本檔。
//
// 兩個媒介的儲存格式刻意不同，resolver 不假設格式：
//   - localStorage：{ value, expiry } 信封，與 utils/storage.js 完全同格式（同一份資料兩邊都讀得回來；
//     app/plugins/07.facebook-pixel.client.js 的 cachePixel 讀的也正是 JSON.parse(x).value）
//   - cookie：裸字串（percent-encoded），沿用 utils/conversionCookies.js 的寫法
//
// ⚠️ 本檔不共用 utils/storage.js 的原因：那支以模組層 `typeof localStorage !== 'undefined'` 判斷可用性，
//    而瀏覽器封鎖站點資料時「讀取 localStorage 屬性本身即拋 SecurityError」；且它的 set() 以 try/catch
//    吞掉錯誤、無回傳值，寫入失敗無法被 REQ-3 的回讀驗證與 REQ-4 的上報看見。
import { REFERRAL_MAX_AGE } from '@/utils/referralParams'

const getLocalStorage = () => {
  // 存取 window.localStorage 屬性本身就可能 throw（Chrome「封鎖所有網站資料」、受限 WebView）
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch (error) {
    return null
  }
}

const hasDocument = () => typeof document !== 'undefined'

const readCookie = (name) => {
  if (!hasDocument()) return null
  const pair = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${name}=`))
  if (!pair) return null
  try {
    return decodeURIComponent(pair.slice(name.length + 1)) || null
  } catch (error) {
    // cookie 為外部可寫狀態：不合法 percent escape 會使 decodeURIComponent 拋 URIError，壞值視為不存在
    return null
  }
}

const isSecureContext = () => typeof window !== 'undefined' && window.location.protocol === 'https:'

export const referralIo = {
  storageGet: (key) => {
    const ls = getLocalStorage()
    if (!ls) return null
    const raw = ls.getItem(key)
    if (!raw) return null
    let parsed
    try {
      parsed = JSON.parse(raw)
    } catch (error) {
      // 同名裸字串（非信封）→ 視為無值，交由下一層 fallback
      return null
    }
    if (!parsed || typeof parsed !== 'object') return null
    if (parsed.expiry && Date.now() > parsed.expiry) {
      ls.removeItem(key)
      return null
    }
    return 'value' in parsed ? parsed.value : null
  },
  storageSet: (key, value, options) => {
    const ls = getLocalStorage()
    // 回傳 false 無法被 persistReferral 區分，直接 throw 讓它標記為 'throw'
    if (!ls) throw new Error(`localStorage unavailable: ${key}`)
    const item = { value: String(value), expiry: Date.now() + (options?.maxAge ?? REFERRAL_MAX_AGE) * 1000 }
    ls.setItem(key, JSON.stringify(item))
  },
  storageRemove: (key) => {
    const ls = getLocalStorage()
    if (!ls) throw new Error(`localStorage unavailable: ${key}`)
    ls.removeItem(key)
  },
  cookieGet: (key) => readCookie(key),
  cookieSet: (key, value, options) => {
    if (!hasDocument()) throw new Error(`document unavailable: ${key}`)
    const attrs = [
      `${key}=${encodeURIComponent(String(value))}`,
      `path=${options?.path || '/'}`,
      `max-age=${options?.maxAge ?? REFERRAL_MAX_AGE}`,
      'SameSite=Lax',
    ]
    // SameSite 不可用 Strict：第三方登入離站再回站會讀不到；正式站補 Secure，不設 HttpOnly（前端需讀取）
    if (isSecureContext()) attrs.push('Secure')
    document.cookie = attrs.join('; ')
  },
  cookieRemove: (key) => {
    if (!hasDocument()) throw new Error(`document unavailable: ${key}`)
    document.cookie = `${key}=; path=/; max-age=0`
  },
}

export const createReferralIo = (search) => ({
  search: search === undefined ? (typeof window !== 'undefined' ? window.location.search : '') : search,
  ...referralIo,
})
